import { useEffect } from 'react'

import { useSetState } from '@domain-group/react-hooks'

import useAgencySelector from '@hooks/useAgencySelector'
import useCurrentUser from '@hooks/useCurrentUser'
import useRoles from '@hooks/useRoles'
import useUser, { useUserForm } from '@hooks/useUser'

import {
  agencyRoles, brandRoles, providerRoles, supportRoles,
} from '@functions/role'
import { isAgencyUser, isBrandUser, isProviderUser } from '@functions/user'
import { UserModel } from '@domain-group/realhub-backend-api-types'
import { RoleModel } from '@domain-group/realhub-backend-api-types'

const groupedRoles = {
  agency: {
    roleTitles: agencyRoles,
    key: 'agencies',
  },
  brand: {
    roleTitles: brandRoles,
    key: 'brands',
  },
  provider: {
    roleTitles: providerRoles,
    key: 'providers',
  },
  support: {
    roleTitles: supportRoles,
    key: 'support',
  },
}

const customRequiredFields = [
  { key: 'mobile_number' },
  { key: 'password', validation: (value: string) => value.length > 7, invalidMessage: 'Must be 8 characters' },
  {
    key: 'password_confirmation',
    validation: (value: string, { password }: { password: string }) => value === password,
    invalidMessage: 'Passwords don\'t match',
  },
  { key: 'time_zone' },
]

const filterRoles = (rolesArray: RoleModel[], roleTitles: string[]) => {
  if (!roleTitles) return rolesArray
  return rolesArray.filter(role => roleTitles.includes(role.title))
}

const setEntityKey = (role_id: number, roles: RoleModel[], setState: (state: any) => void) => {
  const role = roles[role_id] || {}
  const { title } = role

  let entityKey = ''

  if (isAgencyUser(title)){
    entityKey = 'agencies'
  } else if (isBrandUser(title)){
    entityKey = 'brands'
  } else if (isProviderUser(title)){
    entityKey = 'providers'
  }

  setState({ entityKey })
}

const defaultState = {
  entityKey: '',
}

function useCreateUserModal(initUser: Partial<UserModel> = {}) {
  const [state, setState] = useSetState(defaultState)
  const { entityKey } = state

  const { isMasterAdmin, isAgencyAdmin } = useCurrentUser()

  const { selectedAgencyId } = useAgencySelector()

  const { association_type } = initUser
  const groupKey = association_type || (isMasterAdmin ? undefined : 'support')
  // @ts-expect-error
  const roleGroup = groupedRoles[groupKey?.toLowerCase()] || {}

  const prefixRoute = isAgencyAdmin ? `agencies/${selectedAgencyId}/manage` : undefined

  const userPayload = useUser(initUser, { generateUrlsOptions: { prefixRoute } })
  const {
    callbacks,
    creating,
  } = userPayload

  const { filteredRoles, roles } = useRoles()

  const userForm = useUserForm(initUser, { customRequiredFields, validateOn: 'change' })
  const {
    entityState: { association_id, role_id },
    saveEnabled: formSaveEnabled,
  } = userForm

  useEffect(() => {
    if (role_id) setEntityKey(role_id, roles, setState)
  }, [role_id])

  useEffect(() => {
    if (association_type){
      setState({ entityKey: roleGroup.key || '' })
    }
  }, [entityKey])

  const saveEnabled = formSaveEnabled && (entityKey ? !!association_id : true)

  return {
    callbacks,
    creating,
    entityKey,
    filteredRoles: filterRoles(filteredRoles, roleGroup.roleTitles),
    saveEnabled,
    userForm,
  }
}

export default useCreateUserModal
